import React, { useEffect, useRef, useState } from 'react';
import { pickChangedShallow } from '@/utils/patchFromDirty';
import { createPortal } from 'react-dom';
import { X, Check, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
	AlertContainer,
	Button,
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui'; // Adicionados componentes de Select
import { useModal } from '@/hooks';
import { useCreateClub, usePatchClub } from '@/services/mutations/club';

const CLUB_PRICE_UPDATE_CONFIRM_MESSAGE =
	'Isso irá atualizar o preço da assinatura de todos os usuários que utilizam este clube e será efetivada na renovação da assinatura de cada usuário (cada um possui uma data diferente).';

interface ClubConfigModalProps {
	isOpen: boolean;
	onClose: () => void;
	club?: Clubs.Club;
}

const SHELF_OPTIONS: Clubs.Shelf[] = ['B2B2C', 'B2C'];
const RENDER_TYPE_OPTIONS: Array<{
	label: string;
	value: Clubs.ClubRenderType;
}> = [
	{ label: 'Padrão', value: 'DEFAULT' },
	{ label: 'Destaque', value: 'HIGHLIGHTED' },
];

const toDigits = (value: string) => value.replace(/\D/g, '');

const formatPriceInput = (value: string) => {
	const digits = toDigits(value);
	if (!digits) return '';

	const cents = Number(digits) / 100;
	return cents.toLocaleString('pt-BR', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

/**
 * Número vindo da API (string): pode ser "555.55" (ponto decimal) ou "1.234,56" (BR).
 * Não usar replace(/\./g) quando não há vírgula — senão "555.55" vira 55555.
 */
const parseApiPriceString = (apiPrice: string): number => {
	const s = String(apiPrice).trim();
	if (!s) return NaN;
	if (s.includes(',')) {
		return parseFloat(s.replace(/\./g, '').replace(',', '.'));
	}
	return parseFloat(s);
};

/** Converte valor monetário da API para a máscara do input (dígitos = centavos). */
const formatPriceInputFromApi = (apiPrice: string) => {
	const n = parseApiPriceString(apiPrice);
	if (Number.isNaN(n)) return '';
	return formatPriceInput(String(Math.round(n * 100)));
};

const parsePriceToApi = (formattedPrice: string) => {
	const normalized = formattedPrice.replace(/\./g, '').replace(',', '.');
	return normalized;
};

const SlidingToggle = ({ options, value, onChange, label, disabled }: any) => {
	const activeIndex = options.findIndex((o: any) => o.value === value);
	const count = options.length;
	const labelClass =
		'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-400 block mb-2';

	return (
		<div className="w-full">
			{label && <label className={labelClass}>{label}</label>}
			<div className="relative flex w-full rounded-lg bg-stone-100 p-1">
				<div
					className="absolute bottom-1 top-1 rounded-md bg-white shadow-sm transition-all duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]"
					style={{
						width: `calc((100% - 8px) / ${count})`,
						left: `calc(4px + ${activeIndex} * ((100% - 8px) / ${count}))`,
					}}
				/>
				{options.map((opt: any) => (
					<button
						key={String(opt.value)}
						type="button"
						disabled={disabled}
						onClick={() => onChange(opt.value)}
						className={cn(
							'z-10 flex-1 cursor-pointer rounded-md border-none bg-transparent px-0 py-1.5 text-center text-[10px] font-black uppercase tracking-tight outline-none transition-colors duration-200 focus:outline-none',
							value === opt.value
								? 'text-stone-900'
								: 'text-stone-400 hover:text-stone-600',
						)}
					>
						{opt.label}
					</button>
				))}
			</div>
		</div>
	);
};

export const ClubConfigModal = ({
	isOpen,
	onClose,
	club,
}: ClubConfigModalProps) => {
	const { openModal, setOnConfirm, setConfirmText, setCancelText } =
		useModal();
	const [formData, setFormData] = useState<Clubs.Create>({
		name: '',
		price: '',
		currency: 'BRL',
		description: '',
		slogan: '',
		visible: true,
		shelf: 'B2B2C',
		render_type: 'DEFAULT',
		active: true,
		render_order: 0,
	});

	const [errors, setErrors] = useState<Record<string, string>>({});
	const initialPatchRef = useRef<Record<string, unknown> | null>(null);

	const { mutateAsync: createClub, isPending: isCreating } = useCreateClub(
		() => onClose(),
	);
	const { mutateAsync: patchClub, isPending: isPatching } = usePatchClub(() =>
		onClose(),
	);
	const isPending = isCreating || isPatching;

	const buildPayloadForApi = (fd: Clubs.Create) => ({
		...fd,
		price: parsePriceToApi(fd.price),
		render_order: Number(fd.render_order),
	});

	useEffect(() => {
		if (!isOpen) {
			initialPatchRef.current = null;
			return;
		}
		setErrors({});
		if (club) {
			const fd: Clubs.Create = {
				name: club.name,
				price: formatPriceInputFromApi(String(club.price)),
				currency: club.currency || 'BRL',
				description: club.description || '',
				slogan: club.slogan || '',
				visible: club.visible ?? true,
				shelf: club.shelf || 'B2B2C',
				render_type: club.render_type || 'DEFAULT',
				active: club.active ?? true,
				render_order: club.render_order ?? 0,
			};
			setFormData(fd);
			initialPatchRef.current = buildPayloadForApi(fd) as Record<
				string,
				unknown
			>;
		} else {
			setFormData({
				name: '',
				price: '',
				currency: 'BRL',
				description: '',
				slogan: '',
				visible: true,
				shelf: 'B2B2C',
				render_type: 'DEFAULT',
				active: true,
				render_order: 0,
			});
			initialPatchRef.current = null;
		}
	}, [isOpen, club]);

	const validate = () => {
		const newErrors: Record<string, string> = {};
		const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s()]+$/;

		if (!formData.name.trim()) newErrors.name = 'Obrigatório';
		else if (!nameRegex.test(formData.name))
			newErrors.name = 'Letras, números, acentos e parênteses';

		if (!String(formData.price).trim()) newErrors.price = 'Obrigatório';
		if (formData.slogan.trim().length < 3)
			newErrors.slogan = 'Mínimo 3 caracteres';
		if (formData.description.trim().length < 3)
			newErrors.description = 'Mínimo 3 caracteres';

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSave = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!validate()) return;

		const payload = buildPayloadForApi(formData);
		if (club?.id) {
			const body = pickChangedShallow(
				payload as Record<string, unknown>,
				initialPatchRef.current,
			) as Partial<Clubs.Create>;
			if (Object.keys(body).length === 0) {
				onClose();
				return;
			}
			if ('price' in body) {
				setConfirmText('Confirmar atualização');
				setCancelText('Cancelar');
				openModal({
					title: 'Confirmar alteração de preço',
					content: (
						<AlertContainer
							message={CLUB_PRICE_UPDATE_CONFIRM_MESSAGE}
						/>
					),
				});
				setOnConfirm(() => {
					void patchClub({ id: club.id, body });
				});
				return;
			}
			await patchClub({ id: club.id, body });
		} else {
			await createClub(payload);
		}
	};

	if (!isOpen) return null;

	const labelClass =
		'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-400';
	const inputBaseClass =
		'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm font-bold text-stone-600 outline-none transition-all box-border disabled:opacity-50';

	const getFieldClass = (fieldName: string) =>
		cn(
			inputBaseClass,
			errors[fieldName]
				? 'border-rose-500 bg-rose-50/30 focus:border-rose-600'
				: 'border-stone-200 focus:border-stone-400 focus:bg-white',
		);

	const ErrorMsg = ({ field }: { field: string }) =>
		errors[field] ? (
			<span className="animate-in fade-in slide-in-from-top-1 ml-1 mt-1 flex items-center gap-1 text-[10px] font-bold text-rose-500">
				<AlertCircle size={10} /> {errors[field]}
			</span>
		) : null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<form
					onSubmit={handleSave}
					noValidate
					className="flex flex-col overflow-hidden"
				>
					<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-8 py-6">
						<h2 className="text-lg font-black text-stone-900">
							{club ? 'Editar Clube' : 'Novo Clube'}
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="text-stone-400 transition-colors hover:text-stone-600"
						>
							<X size={20} />
						</button>
					</div>

					<div className="flex-1 space-y-6 overflow-y-auto p-8">
						<div className="grid grid-cols-2 gap-4">
							<SlidingToggle
								label="Benefício Ativo?"
								value={formData.active}
								onChange={(val: boolean) =>
									setFormData({ ...formData, active: val })
								}
								options={[
									{ label: 'Sim', value: true },
									{ label: 'Não', value: false },
								]}
							/>
							<SlidingToggle
								label="Origem?"
								value={formData.visible}
								onChange={(val: boolean) =>
									setFormData({ ...formData, visible: val })
								}
								options={[
									{ label: 'Interno', value: true },
									{ label: 'Externo', value: false },
								]}
							/>
						</div>

						<SlidingToggle
							label="Distribuição (Shelf)"
							value={formData.shelf}
							onChange={(val: Clubs.Shelf) =>
								setFormData({ ...formData, shelf: val })
							}
							options={SHELF_OPTIONS.map((opt) => ({
								label:
									opt === 'B2B2C'
										? 'B2B2C (Empresas)'
										: 'B2C (Varejo)',
								value: opt,
							}))}
						/>

						<div className="grid grid-cols-4 gap-4">
							<div className="col-span-3">
								<label className={labelClass}>
									NOME DO CLUBE
								</label>
								<input
									type="text"
									value={formData.name}
									onChange={(e) =>
										setFormData({
											...formData,
											name: e.target.value,
										})
									}
									className={getFieldClass('name')}
									placeholder="Ex: Plumaa Premium"
								/>
								<ErrorMsg field="name" />
							</div>
							<div>
								<label className={labelClass}>Prioridade</label>
								<input
									type="number"
									value={formData.render_order}
									onChange={(e) =>
										setFormData({
											...formData,
											render_order: Number(
												e.target.value,
											),
										})
									}
									className={getFieldClass('render_order')}
								/>
							</div>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className={labelClass}>MOEDA</label>
								<Select
									value={formData.currency}
									onValueChange={(v) =>
										setFormData({
											...formData,
											currency: v as Clubs.Currency,
										})
									}
								>
									<SelectTrigger className="h-[46px] w-full rounded-xl border-stone-200 bg-stone-50 px-4 font-bold text-stone-600 focus:border-stone-400 focus:ring-0">
										<SelectValue className="flex-1 text-left">
											{formData.currency === 'BRL'
												? 'BRL (R$)'
												: formData.currency}
										</SelectValue>
									</SelectTrigger>
									<SelectContent>
										<SelectGroup>
											<SelectItem value="BRL">
												BRL (R$)
											</SelectItem>
										</SelectGroup>
									</SelectContent>
								</Select>
							</div>
							<div>
								<label className={labelClass}>PREÇO</label>
								<input
									type="text"
									value={formData.price}
									onChange={(e) =>
										setFormData({
											...formData,
											price: formatPriceInput(
												e.target.value,
											),
										})
									}
									className={getFieldClass('price')}
									placeholder="99,90"
									inputMode="numeric"
								/>
								<ErrorMsg field="price" />
							</div>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className={labelClass}>
									TIPO DE RENDERIZAÇÃO
								</label>
								<Select
									value={formData.render_type}
									onValueChange={(v) =>
										setFormData({
											...formData,
											render_type:
												v as Clubs.ClubRenderType,
										})
									}
								>
									<SelectTrigger className="h-[46px] w-full rounded-xl border-stone-200 bg-stone-50 px-4 font-bold text-stone-600 focus:border-stone-400 focus:ring-0">
										<SelectValue className="flex-1 text-left">
											{RENDER_TYPE_OPTIONS.find(
												(o) =>
													o.value ===
													formData.render_type,
											)?.label ?? formData.render_type}
										</SelectValue>
									</SelectTrigger>
									<SelectContent>
										<SelectGroup>
											{RENDER_TYPE_OPTIONS.map((opt) => (
												<SelectItem
													key={opt.value}
													value={opt.value}
												>
													{opt.label}
												</SelectItem>
											))}
										</SelectGroup>
									</SelectContent>
								</Select>
							</div>
						</div>

						<div>
							<label className={labelClass}>SLOGAN</label>
							<input
								type="text"
								value={formData.slogan}
								onChange={(e) =>
									setFormData({
										...formData,
										slogan: e.target.value,
									})
								}
								className={getFieldClass('slogan')}
								placeholder="Ex: Seu slogan aqui"
							/>
							<ErrorMsg field="slogan" />
						</div>

						<div>
							<label className={labelClass}>
								DESCRIÇÃO COMERCIAL
							</label>
							<textarea
								rows={3}
								value={formData.description}
								onChange={(e) =>
									setFormData({
										...formData,
										description: e.target.value,
									})
								}
								className={cn(
									getFieldClass('description'),
									'resize-none',
								)}
								placeholder="Descreva as vantagens..."
							/>
							<ErrorMsg field="description" />
						</div>
					</div>

					<div className="flex shrink-0 items-center justify-end gap-6 border-t border-stone-50 p-8">
						<button
							type="button"
							onClick={onClose}
							disabled={isPending}
							className="text-sm font-bold text-stone-400 hover:text-stone-600 disabled:opacity-50"
						>
							Cancelar
						</button>
						<Button
							type="submit"
							text={isPending ? 'Salvando...' : 'Salvar Clube'}
							disabled={isPending}
							className="min-w-[140px] rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800"
							startIcon={
								isPending ? (
									<Loader2 className="size-4 animate-spin" />
								) : (
									<Check className="size-4 stroke-[3]" />
								)
							}
						/>
					</div>
				</form>
			</div>
		</div>,
		document.body,
	);
};
